import { useState, useMemo } from "react";
import { ExternalLink } from "lucide-react";
import MiniSparkline from "@/components/MiniSparkline";
import { TickingPrice } from "@/components/TickingPrice";
import type { LiveMarket } from "@/hooks/useLiveMarkets";

export const LiveMarketCard = ({ market }: { market: LiveMarket }) => {
  const [expanded, setExpanded] = useState(false);

  const momentum = useMemo(() => {
    if (!market.history || market.history.length < 2) return 0;
    return market.history[market.history.length - 1].v - market.history[0].v;
  }, [market.history]);

  const hasHistory = market.history && market.history.length > 1;
  const safeProb = Number(market.probability) || 50;
  const now = Math.floor(Date.now() / 1000);
  const displayHistory = hasHistory ? market.history : [{ t: now - 86400 * 7, v: safeProb }, { t: now, v: safeProb }];
  const probColor = momentum > 2 ? "text-green-400" : momentum < -2 ? "text-red-400" : "text-foreground";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setExpanded(!expanded)}
      className="flex flex-col h-full min-h-fit rounded-xl border border-border bg-card p-3.5 hover:border-primary/30 transition-colors cursor-pointer outline-none"
    >
      <div className="flex items-start justify-between gap-3 mb-2 shrink-0">
        <div className="flex flex-col flex-1 min-w-0 pr-2">
          <p className="text-[13px] font-bold text-foreground leading-snug line-clamp-2">{market.question}</p>
          {market.topOutcome && !["yes", "no"].includes(market.topOutcome.toLowerCase()) && (
            <span className="text-[11px] mt-0.5 line-clamp-1">
              <span className="text-muted-foreground italic">Leading: </span>
              <span className="font-medium text-primary">{market.topOutcome}</span>
            </span>
          )}
        </div>
        <span className="text-lg font-extrabold leading-none shrink-0">
          <TickingPrice value={market.probability} baseClassName={probColor} />
        </span>
      </div>

      <div className={`mb-1 transition-all duration-300 shrink-0 ${expanded ? "h-16" : "h-6"}`}>
        <MiniSparkline data={displayHistory} positive={momentum >= 0} height={expanded ? 64 : 24} showTooltip={expanded} />
      </div>

      {expanded && market.siblings && market.siblings.length > 0 && (
        <div className="border-t border-border/50 pt-2 pb-1 mt-1 space-y-1">
          <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
            Other Outcomes
          </span>
          {market.siblings.map((s, i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground truncate mr-2">{s.label}</span>
              <span className="text-[11px] font-semibold text-muted-foreground shrink-0">{s.probability}%</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-auto pt-2 shrink-0 w-full gap-3">
        <a
          href={market.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 min-w-0 min-h-[24px] shrink hover:text-primary transition-colors group/link"
        >
          <ExternalLink className="w-2.5 h-2.5 text-muted-foreground/60 group-hover/link:text-primary shrink-0" />
          <span className="text-[10px] text-muted-foreground group-hover/link:text-primary truncate font-semibold">
            View on {market.platform}
          </span>
        </a>
        <div className="flex items-center gap-2.5 shrink-0">
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{market.volumeTotal} vol</span>
          <span className="text-[10px] text-primary font-semibold whitespace-nowrap">
            {expanded ? "Collapse" : "Details"}
          </span>
        </div>
      </div>
    </div>
  );
};
